/**
 * CacheManager
 * A centralized manager for storing, retrieving, and deleting cached data.
 * Uses localStorage for web. Can be extended for React Native AsyncStorage.
 */

export const CACHE_KEYS = {
  USER_PROFILE: "user_profile",
  TOKEN: "auth_token",
  SETTINGS: "app_settings",
  LANGUAGE: "app_language",
  STORE: "store",
  SIDE_BAR_STATUS: "sidebarCollapsed",
};

export class CacheManager {
  private static instance: CacheManager;

  private constructor() {}

  /**
   * Singleton instance
   */
  public static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  /**
   * Save data to cache
   * @param key string key
   * @param value any value (object will be stringified)
   */
  public set<T>(key: string, value: T): void {
    try {
      const stringValue =
        typeof value === "string" ? value : JSON.stringify(value);
      localStorage.setItem(key, stringValue);
    } catch (error) {
      console.error(`CacheManager: Failed to save key "${key}"`, error);
    }
  }

  /**
   * Get data from cache
   * @param key string key
   * @returns parsed value or null
   */
  public async get<T>(key: string): Promise<T | null> {
    try {
      const value = await localStorage.getItem(key);
      if (!value) return null;
      try {
        return JSON.parse(value) as T;
      } catch {
        // if value is not JSON
        return value as unknown as T;
      }
    } catch (error) {
      console.error(`CacheManager: Failed to read key "${key}"`, error);
      return null;
    }
  }

  /**
   * Remove data from cache
   * @param key string key
   */
  public remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`CacheManager: Failed to remove key "${key}"`, error);
    }
  }

  /**
   * Clear all cache
   */
  public clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error("CacheManager: Failed to clear cache", error);
    }
  }

  /**
   * Check if key exists
   * @param key string key
   */
  public has(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }
}
